import { type FC } from 'react'
import { type UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import isVAT, { type VATCountryCode } from 'validator/lib/isVAT'

import { Heading } from 'base-ui'

import { type MarketplacePartnerRegisterPageFormV3Values } from '../../../hooks/marketplacePartnerRegisterPageFormV3RHFModel'
import { type MarketplacePartnerRegisterPageDictionaryOption } from '../../../hooks/useMarketplacePartnerRegisterPageGetDictionaryOptionsByName'

import { MarketplacePartnerRegisterPageFormV3InputField } from './MarketplacePartnerRegisterPageFormV3InputField'
import { MarketplacePartnerRegisterPageFormV3SelectField } from './MarketplacePartnerRegisterPageFormV3SelectField'

type MarketplacePartnerRegisterPageFormV3BasicInformationsProps = {
  countryCodeOptions?: MarketplacePartnerRegisterPageDictionaryOption[]
  form: UseFormReturn<MarketplacePartnerRegisterPageFormV3Values>
}

const COUNTRY_CODE_NUMBER_OF_CHARACTERS = 2
export const MarketplacePartnerRegisterPageFormV3BasicInformations: FC<
  MarketplacePartnerRegisterPageFormV3BasicInformationsProps
> = ({ countryCodeOptions, form }) => {
  const intl = useIntl()
  const missingError = intl.formatMessage({ id: 'form.error.input.missing' })
  const invalidError = intl.formatMessage({ id: 'form.error.input.invalid' })

  const validateEuVatNumber = function validateEuVatNumber(
    vatNumber: string,
  ): string | true {
    const countryCode = vatNumber.slice(0, COUNTRY_CODE_NUMBER_OF_CHARACTERS)

    if (!countryCode) {
      return invalidError
    }

    let isValidVatNumber = false

    try {
      isValidVatNumber = isVAT(vatNumber, countryCode as VATCountryCode)
    } catch {
      return invalidError
    }

    return isValidVatNumber || invalidError
  }

  return (
    <div className="flex flex-col gap-10">
      <Heading level={2} size="l">
        <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.title" />
      </Heading>
      <div className="text-m">
        <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.description" />
      </div>

      <div className="flex flex-col gap-4">
        <div className="text-l">
          <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.contactDetails.title" />
        </div>
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="firstName"
          form={form}
          label={<FormattedMessage id="general.label.firstName" />}
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="lastName"
          form={form}
          label={<FormattedMessage id="general.label.surname" />}
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="email"
          form={form}
          label={<FormattedMessage id="general.label.email" />}
          required
          rules={{ required: missingError }}
          type="email"
        />
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="phoneNumber"
          form={form}
          label={<FormattedMessage id="general.label.phoneNumber" />}
          required
          rules={{ required: missingError }}
          type="tel"
        />
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="name"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.basicInformation.companyInformation.companyName" />
          }
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="vatNumber"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.v3.vatNumber.label" />
          }
          required
          rules={{ required: missingError, validate: validateEuVatNumber }}
        />
        <MarketplacePartnerRegisterPageFormV3SelectField
          fieldName="countryCode"
          form={form}
          label={
            <FormattedMessage id="marketplacePartnerRegisterPage.v3.countryCode.label" />
          }
          optionEntries={countryCodeOptions?.map((option) => ({
            label: option.label,
            value: option.key,
          }))}
          required
          rules={{ required: missingError }}
        />
        <MarketplacePartnerRegisterPageFormV3InputField
          fieldName="website"
          form={form}
          label={<FormattedMessage id="general.label.companyWebsite" />}
          required
          rules={{ required: missingError }}
        />
      </div>
    </div>
  )
}
